import {Box, Button, MenuItem, MenuList, Popover, Typography} from '@mui/material'
import {useCallback, useRef, useState} from 'react'
import {maxLength, required, TextInput, useTranslate} from 'react-admin'
import {useFormContext} from 'react-hook-form'
import {makeStyles} from 'tss-react/mui'

import EmojiPickerButton from '../../components/chat/ChatToolbar/EmojiPickerButton'
import {useCompileChatMessageText} from '../../hooks/useCompileChatMessageText'
import {AppTheme} from '../../theme'

const TemplateBuilderTextToolbar = () => {
  const {classes: styles} = useStyles({})
  const translate = useTranslate()
  const textInputRef = useRef<HTMLInputElement>()
  const [placeholderButtonAnchorEl, setPlaceholderButtonAnchorEl] = useState(null)
  const {setValue, watch} = useFormContext()
  const formValues = watch()
  const setInputText = useCallback(text => setValue('text', text), [setValue])
  const inputText = formValues.text ?? ''
  const type = formValues.type
  const {placeholderTypeToLabel} = useCompileChatMessageText()
  const openPlaceholderMenu = useCallback(
    e => setPlaceholderButtonAnchorEl(e.currentTarget), [setPlaceholderButtonAnchorEl]
  )
  const closePlaceholderMenu = useCallback(
    () => setPlaceholderButtonAnchorEl(null), [setPlaceholderButtonAnchorEl]
  )
  const insertFreeTextPlaceholder = () =>
    insertAtCurrentCursorPosition(
      `{{${
        translate('chat_message_template_placeholder_types.FREE_TEXT')
      }}}`
    )
  const insertSmartPlaceholder = placeholderCode =>
    insertAtCurrentCursorPosition(
      `{{%${
        translate(`chat_message_template_placeholder_types.${placeholderCode}`)
      }%}}`
    )
  const insertAtCurrentCursorPosition = (
    insertedText, {hasPrependedEmptySpace = true} = {}
  ) => {
    const selectionStart = textInputRef.current?.selectionStart as number
    const startText = inputText.slice(0, selectionStart)
    const endText = inputText.slice(selectionStart)
    setInputText(
      // Prepend a space before and after the text to insert if none is present yet.
      // We do this because users often overlook that placeholders, and such as words,
      // should be separated by spaces.
      // Otherwise, they end up with this: "Hello {{firstName}}{{lastName}}".
      `${startText}${
        hasPrependedEmptySpace && (startText.length && startText.substring(-1) !== ' ') ?
          ' ' : ''
      }` +
      `${insertedText}` +
      `${
        (
          endText.length &&
          (endText.substring(0, 1) !== ' ') &&
          // Do not insert a trailing space after the inserted text if the next character
          // is a punctuation(., ,, !, ?, " and so on). The punctuation "{" is excluded
          // because we want a space between consecutive placeholders.
          // There is no leading space before punctuations as in :
          // "Hello {{firstname}}! How are you? Greetings {{merchant}}, from Berlin"
          !PUNCTUATION_REGEX.test(endText.substring(0, 1))
        ) ? ' ' : ''
      }${endText}`
    )
    // The text input is not set immediately, hence it's not possible to set the
    // new "selectionStart". A workaround is to set it asynchronously.
    setTimeout(
      () => {
        // Set the new cursor position to allow the user to set multiple placeholders on
        // the fly. The new cursor position is the position where
        // the currently inserted text ends.
        textInputRef.current?.setSelectionRange(
          `${startText}${insertedText}`.length + 1,
          `${startText}${insertedText}`.length + 1,
        )
        textInputRef.current?.focus()
      },
      10
    )
    closePlaceholderMenu()
  }
  const applyFontStyle = useCallback((style: 'bold' | 'italic') => {
    const selectionStart = textInputRef.current?.selectionStart as number
    const selectionEnd = textInputRef.current?.selectionEnd as number
    const fontStyleCharacter = {bold: '*', italic: '_'}[style]
    setInputText(
      inputText.split('')
        .toSpliced(selectionEnd, 0, fontStyleCharacter)
        .toSpliced(selectionStart, 0, fontStyleCharacter)
        .join('')
    )
    setTimeout(
      () => {
        // Set the new cursor position at the end so the user can continue typing.
        // The new cursor position is one step before the end of fontStyleCharacter.
        textInputRef.current?.setSelectionRange(
          selectionEnd + fontStyleCharacter.length,
          selectionEnd + fontStyleCharacter.length,
        )
        textInputRef.current?.focus()
      },
      0
    )
  }, [inputText, setInputText])
  const extractPlaceholderLabels = useCallback(
    (text: string) => Array.from(text.matchAll(/{{(.*?)}}/g)).map(match => match[1]),
    []
  )
  return (
    <div>
      <Typography component="label">
        {translate('resources.chat_message_templates.create.inputs.body.title')}
      </Typography>
      <div className={styles.inputAndButtonsContainer}>
        <TextInput
          defaultValue="Hello👋"
          inputRef={textInputRef}
          label=""
          placeholder={translate(
            'resources.chat_message_templates.create.inputs.body.label'
          )}
          multiline
          minRows={3}
          source="text"
          validate={[
            required(),
            (text, {type}) => (
              type === 'MARKETING' &&
              // Only allow named placeholders for marketing templates.
              !extractPlaceholderLabels(text ?? '').every(
                label => Object.entries(placeholderTypeToLabel)
                  .filter(([type]) => type !== 'FREE_TEXT')
                  .map(([, placeholderLabel]) => `%${placeholderLabel}%`)
                  .includes(label)
              ) ? translate(
                  'resources.chat_message_templates.create.inputs.body.validations.' +
                'freeTextNotAllowedInCampaignTemplate'
                ) : undefined
            ),
            maxLength(
              MAX_BODY_TEXT_LENGTH,
              translate('validations.maxCharacters', {max: MAX_BODY_TEXT_LENGTH})
            ),
            v => (/ {5,}/).test(v) ? translate(
              'resources.chat_message_templates.create.inputs.body.validations.' +
              'consecutiveSpaces'
            ) : null,
            v => (/\t/).test(v) ? translate(
              'resources.chat_message_templates.create.inputs.body.validations.tabs'
            ) : null,
            v => BODY_PLACEHOLDERS_PATTERN.test(v.trim()) ? translate(
              'resources.chat_message_templates.create.inputs.body.' +
              'validations.placeholdersNotAllowedAtStartOrEnd',
            ) : null,
          ]}
        />
        <Typography component="p" textAlign="end" variant="caption" color="textSecondary">
          {(formValues.text ?? "").length}/{MAX_BODY_TEXT_LENGTH}
        </Typography>
        <Box className={styles.buttonsContainer}>
          <EmojiPickerButton
            onSelect={emoji => insertAtCurrentCursorPosition(
              emoji, {hasPrependedEmptySpace: false}
            )}
          />
          <Button onClick={() => applyFontStyle('bold')}>
            <Typography fontWeight="bold" fontSize="x-large">
              B
            </Typography>
          </Button>
          <Button onClick={() => applyFontStyle('italic')}>
            <Typography
              fontStyle="italic"
              fontSize="x-large"
              fontFamily="Roboto"
            >
              I
            </Typography>
          </Button>
          <>
            <Button onClick={openPlaceholderMenu}>
              <Typography fontSize="large">
                {"{{}}"}
              </Typography>
            </Button>
            <Popover
              anchorEl={placeholderButtonAnchorEl}
              anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
              onClose={closePlaceholderMenu}
              open={!!placeholderButtonAnchorEl}
              transformOrigin={{horizontal: 'left', vertical: 'top'}}
            >
              <MenuList>
                {
                  (type === 'UTILITY') && (
                    <MenuItem onClick={() => insertFreeTextPlaceholder()}>
                      {translate('chatMessageTemplateEditor.freeText')}
                    </MenuItem>
                  )
                }
                {
                  Object.entries(placeholderTypeToLabel).map(
                    ([type, label]) => (
                      type !== 'FREE_TEXT' && (
                        <MenuItem key={type} onClick={() => insertSmartPlaceholder(type)}>
                          {label}
                        </MenuItem>
                      )
                    )
                  )
                }
              </MenuList>
            </Popover>
          </>
        </Box>
      </div>
    </div>
  )
}

const useStyles = makeStyles<any>()((theme: AppTheme) => ({
  buttonsContainer: {
    '& .MuiButton-root': {
      margin: 0,
      minWidth: 0,
      paddingLeft: theme.remSpacing(2),
      paddingRight: theme.remSpacing(2),
    },
    alignItems: 'center',
    cursor: 'text',
    display: 'flex',
  },
  inputAndButtonsContainer: {
    '& .MuiFilledInput-root': {
      '& textarea': {
        paddingLeft: `${theme.remSpacing(1)} !important`,
      },
      '&:after': {
        border: 'none',
      },
      border: 0,
      boxShadow: 'none',
    },
    '& .MuiInputBase-root': {
      paddingTop: 0,
    },
    '&:focus-within': {
      borderColor: theme.palette.info.main,
    },
    border: `2px solid ${theme.palette.disabled.main}`,
    borderRadius: '8px',
    marginTop: theme.remSpacing(1.5),
    padding: `0 ${theme.remSpacing(2)} ${theme.remSpacing(2)}`,
    transition: 'border-color 0.2s',
  },
}))

// The following regex matches any punctuation expect "{".
// See:
// https://www.freecodecamp.org/news/what-is-punct-in-regex-how-to-match-all-punctuation-marks-in-regular-expressions
// https://stackoverflow.com/a/3973782
const PUNCTUATION_REGEX = /^(?![{])\p{P}$/u
const MAX_BODY_TEXT_LENGTH = 1024
const BODY_PLACEHOLDERS_PATTERN =
  /(^[\W]*{{(%)*(\w|\s)+(%)*}})|({{(%)*(\w|\s)+(%)*}}[\W]*$)/

export default TemplateBuilderTextToolbar
